import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { fetchClinics } from "../store/thunks/clinicThunk";
import { fetchClinicResources } from "../store/thunks/resourceThunk";
import { fetchResourceSchedules } from "../store/thunks/resourceScheduleThunk";
import { format, addDays, subDays, startOfWeek, endOfWeek } from "date-fns";
import ResourceSchedule from "./schedulePage/resourceComponents/resourceSchedule";
import ResourceInfo from "./schedulePage/resourceComponents/resourceInfo";

function ResourceSchedulePage() {
  const dispatch = useDispatch();
  const user = useSelector((state) => state.auth.user);
  const clinics = useSelector((state) => state.clinics.clinics);
  const resources = useSelector((state) => state.resources.resources);
  const resourceSchedules = useSelector((state) => state.resourceSchedule.schedules);
  
  const [selectedClinic, setSelectedClinic] = useState(null);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedResource, setSelectedResource] = useState(null);

  useEffect(() => {
    // Fetch all clinics first
    dispatch(fetchClinics());
  }, [dispatch]);

  useEffect(() => {
    // Set default clinic
    if (clinics.length > 0 && !selectedClinic) {
      const defaultClinic = clinics.find(clinic => clinic._id === user?.defaultLocation) || clinics[0];
      setSelectedClinic(defaultClinic);
    }
  }, [clinics, user?.defaultLocation, selectedClinic]);

  useEffect(() => {
    // Fetch resources when clinic is selected
    if (selectedClinic?._id) {
      dispatch(fetchClinicResources(selectedClinic._id));
    }
  }, [dispatch, selectedClinic]);

  useEffect(() => {
    // Set default resource when resources are loaded
    if (resources.length > 0 && !selectedResource) {
      setSelectedResource(resources[0]);
    }
  }, [resources, selectedResource]);

  useEffect(() => {
    // Fetch resource schedule when resource and date are selected
    if (selectedResource?._id && selectedClinic?._id) {
      const startDate = startOfWeek(currentDate);
      const endDate = endOfWeek(currentDate);
      dispatch(fetchResourceSchedules({
        clinicId: selectedClinic._id,
        startDate: format(startDate, 'yyyy-MM-dd'),
        endDate: format(endDate, 'yyyy-MM-dd')
      }));
    }
  }, [dispatch, selectedResource, selectedClinic, currentDate]);

  const handleClinicChange = (event) => {
    const clinicId = event.target.value;
    const clinic = clinics.find(c => c._id === clinicId);
    setSelectedClinic(clinic);
    setSelectedResource(null); // Reset resource selection
  };

  const handleResourceChange = (event) => {
    const resourceId = event.target.value;
    const resource = resources.find(r => r._id === resourceId);
    setSelectedResource(resource);
  };

  const handleDateNavigation = (direction) => {
    if (direction === 'prev') {
      setCurrentDate(subDays(currentDate, 1));
    } else if (direction === 'next') {
      setCurrentDate(addDays(currentDate, 1));
    }
  };

  const handleDateChange = (event) => {
    setCurrentDate(new Date(event.target.value));
  };

  const handleTimeClick = (time) => {
    console.log('Time clicked:', time, 'for resource:', selectedResource?.name);
    // TODO: Implement booking logic
  };

  const handleTimeContextMenu = (e, time) => {
    e.preventDefault();
    console.log('Right click on time:', time);
    // TODO: Implement context menu
  };

  // Get events for the selected resource and date
  const getEventsForDate = () => {
    const dateString = format(currentDate, 'yyyy-MM-dd');
    return resourceSchedules.filter(schedule => 
      format(new Date(schedule.date), 'yyyy-MM-dd') === dateString &&
      schedule.resourceId === selectedResource?._id
    );
  };

  return (
    <div className="px-4 sm:px-6 lg:px-8">
      {/* Header with Clinic and Resource Selectors */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-2xl font-semibold text-gray-900">Resource Schedule</h1>
            <p className="mt-1 text-sm text-gray-600">
              View and manage resource availability and bookings
            </p>
          </div>
          
          {/* Controls */}
          <div className="flex items-center space-x-4">
            {/* Clinic Selector */}
            <div className="flex items-center space-x-2">
              <label htmlFor="clinic-select" className="text-sm font-medium text-gray-700">
                Clinic:
              </label>
              <select
                id="clinic-select"
                value={selectedClinic?._id || ''}
                onChange={handleClinicChange}
                className="min-w-48 rounded-md border border-gray-300 bg-white px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
              >
                <option value="">Select a clinic...</option>
                {clinics.map((clinic) => (
                  <option key={clinic._id} value={clinic._id}>
                    {clinic.name}
                  </option>
                ))}
              </select>
            </div>

            {/* Resource Selector */}
            {selectedClinic && (
              <div className="flex items-center space-x-2">
                <label htmlFor="resource-select" className="text-sm font-medium text-gray-700">
                  Resource:
                </label>
                <select
                  id="resource-select"
                  value={selectedResource?._id || ''}
                  onChange={handleResourceChange}
                  className="min-w-48 rounded-md border border-gray-300 bg-white px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                >
                  <option value="">Select a resource...</option>
                  {resources.map((resource) => (
                    <option key={resource._id} value={resource._id}>
                      {resource.name}
                    </option>
                  ))}
                </select>
              </div>
            )}
          </div>
        </div>

        {/* Date Navigation */}
        {selectedResource && (
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <button
                onClick={() => handleDateNavigation('prev')}
                className="px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
              >
                Previous Day
              </button>
              
              <input
                type="date"
                value={format(currentDate, 'yyyy-MM-dd')}
                onChange={handleDateChange}
                className="rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
              />
              
              <button
                onClick={() => handleDateNavigation('next')}
                className="px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
              >
                Next Day
              </button>
            </div>
            
            <div className="text-lg font-medium text-gray-900">
              {format(currentDate, 'EEEE, MMMM d, yyyy')}
            </div>
          </div>
        )}
      </div>

      {/* Main Content */}
      {!selectedClinic ? (
        <div className="text-center py-12">
          <div className="text-gray-500">Please select a clinic to view resources.</div>
        </div>
      ) : !selectedResource ? (
        <div className="text-center py-12">
          <div className="text-gray-500">
            {resources.length === 0 
              ? `No resources available for ${selectedClinic.name}.`
              : 'Please select a resource to view its schedule.'
            }
          </div>
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow border border-gray-200">
          <div className="flex">
            {/* Resource Info Sidebar */}
            <div className="w-64 relative">
              <ResourceInfo 
                resource={selectedResource}
                calendarDate={currentDate}
                dailyNote={null} // TODO: Implement daily notes for resources
                onNoteUpdate={() => {}} // TODO: Implement note update
                onNoteDelete={() => {}} // TODO: Implement note delete
              />
            </div>
            
            {/* Schedule Grid */}
            <div className="flex-1">
              <ResourceSchedule
                resource={selectedResource}
                currentDate={currentDate}
                events={getEventsForDate()}
                showWorkingHours={true}
                onTimeClick={handleTimeClick}
                onTimeContextMenu={handleTimeContextMenu}
                onTimeHover={(time) => {}} // TODO: Implement hover functionality
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ResourceSchedulePage;